/**
 * App — wiring.
 *
 * Deliberately the only module that knows where data comes from. The viewer
 * components take a Flight and nothing else, which is what makes the same
 * viewer reusable inside Dronedesk against a different source.
 */

import { Flight, fmt, units } from './contract.js';
import { Library, hashFile } from './store.js';
import { FlightProfile } from './profile.js';
import { BatteryChart } from './battery.js';
import { SignalChart } from './signal.js';
import { AttitudeChart } from './attitude.js';
import { TelemetryTable } from './table.js';
import { Gimbals } from './sticks.js';
import { AttitudeModel } from './attitude-model.js';
import { FlightMap } from './map.js';
import { Toasts } from './toasts.js';
import { download, exportFilename } from './export.js';
import { requestVerification } from './verify.js';
import { confirmAction } from './confirm.js';
import { ensureTurnstile, armTurnstile } from './turnstile-loader.js';
import { warmMapLibre } from './maplibre-loader.js';

/**
 * Where the API lives, relative to this page.
 *
 * Read from a meta tag rather than hardcoded, because the front end and the
 * endpoints are deployed as two separate directories and how they are joined
 * up depends on the web server. A vhost with an Alias puts them at /api; a
 * subdirectory deployment puts them somewhere else entirely. One line in
 * index.html rather than a code change.
 */
const API_BASE = (
  document.querySelector('meta[name="api-base"]')?.content || '/api'
).replace(/\/$/, '');

// The .php is deliberate.
//
// Extensionless URLs only resolve to a file if the server is rewriting them
// or has MultiViews on. WampServer enables MultiViews by default and most
// production Apache does not, so a request that worked all the way through
// development returned a flat 404 on the first real deployment. Asking for
// the file by name removes the dependency entirely: no rewrite rules, no
// MultiViews, nothing to configure.
const PROCESS_ENDPOINT = `${API_BASE}/process.php`;
const EXPORT_ENDPOINT = `${API_BASE}/export.php`;
const ANALYSE_ENDPOINT = `${API_BASE}/analyse.php`;
const QUOTA_ENDPOINT = `${API_BASE}/quota.php`;
const VERIFY_SEND_ENDPOINT = `${API_BASE}/verify-send.php`;
const VERIFY_CONFIRM_ENDPOINT = `${API_BASE}/verify-confirm.php`;
const REPORT_ENDPOINT = `${API_BASE}/report.php`;
const PROMPT_VERSION = '5';

/**
 * Turnstile — the human check on the billable endpoints.
 *
 * A visible managed widget sits by the dropzone and solves quietly on load,
 * handing us a token. We spend that token on upload and reset for the next one,
 * because a token is single use and lasts five minutes; an expiry re-arms in
 * the background. The verify dialog renders its own widget when it opens.
 *
 * With no sitekey set the whole thing is inert: token() returns an empty
 * string, the server (with an empty secret) accepts it, and nothing renders.
 * That is the same off switch as the server side, read from the same pair of
 * places, so the two cannot drift apart.
 */
const TURNSTILE_SITEKEY =
  document.querySelector('meta[name="turnstile-sitekey"]')?.content?.trim() || '';

// 'interaction-only' keeps the widget invisible unless Cloudflare decides a
// challenge is actually warranted, so most people never see it. Configurable
// via the meta tag; defaults here rather than hard-failing if it is absent.
const TURNSTILE_APPEARANCE =
  document.querySelector('meta[name="turnstile-appearance"]')?.content?.trim() || 'interaction-only';

const turnstileGate = (() => {
  let widgetId = null;
  let current = '';
  let waiters = [];

  const settle = (token) => {
    current = token || '';

    if (current) {
      const pending = waiters;
      waiters = [];
      pending.forEach((resolve) => resolve(current));
    }
  };

  return {
    enabled: () => TURNSTILE_SITEKEY !== '',

    /** Render the widget into a container, once. No-op when off. */
    render(target) {
      if (!TURNSTILE_SITEKEY || typeof turnstile === 'undefined' || widgetId !== null) {
        return;
      }

      const el = typeof target === 'string' ? document.querySelector(target) : target;
      if (!el) return;

      widgetId = turnstile.render(el, {
        sitekey: TURNSTILE_SITEKEY,
        appearance: TURNSTILE_APPEARANCE,
        callback: settle,
        'expired-callback': () => {
          current = '';
          if (widgetId !== null) turnstile.reset(widgetId);
        },
        'error-callback': () => {
          current = '';
        },
      });
    },

    /** The token held right now, without waiting. '' if none yet. */
    peek: () => current,

    /**
     * The current token, waiting briefly for the widget to solve if it has not
     * yet. Resolves to '' on timeout rather than hanging the upload; the server
     * then answers 403 and the user is told to retry, which is the right
     * outcome for a widget that could not solve.
     */
    token(timeoutMs = 8000) {
      if (!TURNSTILE_SITEKEY) return Promise.resolve('');
      if (current) return Promise.resolve(current);

      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          waiters = waiters.filter((w) => w !== push);
          resolve('');
        }, timeoutMs);

        const push = (token) => {
          clearTimeout(timer);
          resolve(token);
        };

        waiters.push(push);
      });
    },

    /** Spend the current token and arm the next, whatever the outcome. */
    reset() {
      current = '';
      if (widgetId !== null && typeof turnstile !== 'undefined') {
        turnstile.reset(widgetId);
      }
    },
  };
})();

// Turnstile's api.js calls window.onTurnstileReady on load, which is defined
// inline in index.html so Cloudflare always finds it. It sets __turnstileReady
// and calls __renderTurnstile if present. Register our renderer here and run it
// now if the script already loaded — either order works.
//
// api.js is no longer a tag in the head: it is fetched on the first sign that
// an upload is coming, so most visits never pay for it. That makes the bridge
// the normal render path rather than a race, since the script cannot arrive
// before this module has run.
window.__renderTurnstile = () => turnstileGate.render('#turnstile-upload');

if (window.__turnstileReady) {
  window.__renderTurnstile();
}

/**
 * Fetch Turnstile and put the upload widget on screen, once.
 *
 * Called from the first pointer, keyboard or drag contact with the dropzone.
 * The widget needs a moment to solve, and that moment overlaps with the person
 * choosing a file, so by the time the upload runs a token is normally already
 * in hand. Every step is idempotent, so calling this repeatedly is free.
 */
async function startTurnstile() {
  if (!turnstileGate.enabled()) return;
  if (!(await ensureTurnstile())) return;

  turnstileGate.render('#turnstile-upload');
}

class App {
  constructor() {
    this.library = new Library();
    this.flight = null;
    this.hash = null;
    this.map = new FlightMap(document.getElementById('map'));
    this.components = {};

    this.el = {
      app: document.querySelector('.app'),
      landing: document.getElementById('landing'),
      viewer: document.getElementById('viewer'),
      dropzone: document.getElementById('dropzone'),
      fileInput: document.getElementById('file-input'),
      libraryList: document.getElementById('library-list'),
      usage: document.getElementById('usage'),
      status: document.getElementById('status'),
      headerFile: document.getElementById('header-file'),
      stats: document.getElementById('stats'),
      details: document.getElementById('details'),
      analysis: document.getElementById('analysis'),
      warnings: document.getElementById('warnings'),
      sidebar: document.getElementById('sidebar'),
      readouts: document.getElementById('readouts'),
      toasts: document.getElementById('toasts'),
      gimbals: document.getElementById('gimbals'),
      attitudeModel: document.getElementById('attitude-model'),
      profile: document.getElementById('profile'),
      batteryProfile: document.getElementById('battery-profile'),
      signalProfile: document.getElementById('signal-profile'),
      attitudeProfile: document.getElementById('attitude-profile'),
      stripToggle: document.getElementById('strip-toggle'),
      table: document.getElementById('table'),
    };
  }

  async start() {
    if (!this.checkSecureContext()) return;

    this.setHeaderControls(false);
    this.bind();
    this.initResizers();
    this.refreshQuota();
    await this.library.requestPersistence();
    await this.refreshLibrary();
  }

  /**
   * Build the map, once, on first use.
   *
   * MapLibre is 206 KB and the landing page has no map on it, so neither the
   * library nor the map instance exist until a flight is actually opened.
   * Holding the promise rather than a boolean means two flights opened in
   * quick succession share one build instead of racing to make two.
   */
  ensureMap() {
    if (!this._mapReady) {
      this._mapReady = this.map.init();
    }

    return this._mapReady;
  }

  /**
   * Uploads are hashed with crypto.subtle, which browsers only expose in a
   * secure context: HTTPS, or plain HTTP on localhost. Served from any other
   * hostname the API is simply undefined, and without this check the failure
   * surfaces as an unrelated error deep in the upload path.
   */
  checkSecureContext() {
    if (window.isSecureContext && window.crypto && window.crypto.subtle) return true;

    const host = window.location.hostname;

    // Reported through the status bar when the landing markup is absent,
    // otherwise the secure-context warning is itself swallowed by a null
    // reference and the page dies with an unrelated error.
    if (!this.el.landing) {
      this.status(`This page needs to be served over HTTPS or from localhost, not ${host}.`, false, true);
      return false;
    }

    this.el.landing.innerHTML = `
      <div style="grid-column:1/-1;max-width:620px">
        <h1 style="font-size:22px;font-weight:600;margin:0 0 12px">
          This page needs a secure origin
        </h1>
        <p style="color:var(--text-dim);line-height:1.6">
          Flight logs are hashed in your browser before upload, and the browser
          only provides that capability over HTTPS or on localhost.
          This page is being served from <code>${escapeHtml(host)}</code>.
        </p>
        <p style="color:var(--text-dim);line-height:1.6">
          Reach it on <code>http://localhost:${window.location.port || 80}</code>
          instead, or on a hostname ending in <code>.localhost</code>, or set up HTTPS.
        </p>
      </div>`;

    return false;
  }

  bind() {
    const zone = this.el.dropzone;

    // Turnstile loads on the first sign that an upload is coming rather than on
    // every page load. Focus alone would be too narrow: most people drag a file
    // straight on without ever focusing the zone, so the drag counts too.
    armTurnstile(zone, startTurnstile);

    // The picker must be opened synchronously from the gesture, or Safari
    // blocks it. So the allowance is checked against a cached figure fetched
    // on load and refreshed after every upload, rather than awaiting a request
    // here. The server re-checks and remains the authority; this only spares
    // someone the indignity of choosing a file and then being told no.
    const open = () => {
      if (!this.allowUpload()) return;
      this.el.fileInput.click();
    };

    zone.addEventListener('click', open);
    zone.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
    });

    ['dragenter', 'dragover'].forEach((type) =>
      zone.addEventListener(type, (e) => { e.preventDefault(); zone.classList.add('over'); })
    );
    ['dragleave', 'drop'].forEach((type) =>
      zone.addEventListener(type, (e) => { e.preventDefault(); zone.classList.remove('over'); })
    );

    zone.addEventListener('drop', (e) => {
      if (!this.allowUpload()) return;
      const file = e.dataTransfer.files[0];
      if (file) this.handleFile(file);
    });

    this.el.fileInput.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (file) this.handleFile(file);
      e.target.value = '';
    });

    document.getElementById('close-flight').addEventListener('click', () => this.closeFlight());

    document.getElementById('unit-toggle').addEventListener('click', () => {
      units.setSystem(units.system === 'metric' ? 'imperial' : 'metric');

      // Write into the label span, not the button. Setting textContent on the
      // button would delete the icon with it, and e.target is the icon itself
      // when that is what was clicked.
      const label = document.querySelector('#unit-toggle .unit-label');
      if (label) label.textContent = units.system === 'metric' ? 'Metric' : 'Imperial';
      if (!this.flight) return;
      this.renderSidebar();
      this._readout = null;
      if (this.components.table) this.components.table.refreshUnits();
      this.setIndex(this.components.profile ? this.components.profile.index : 0);
    });

    document.getElementById('download-pdf').addEventListener('click', () => this.downloadReport());

    document.getElementById('download-csv').addEventListener('click', () => this.exportLog('csv'));

    const kml = document.getElementById('download-kml');
    if (kml) kml.addEventListener('click', () => this.exportLog('kml'));

    // Keyboard shortcuts, but not while the user is typing in something
    document.addEventListener('keydown', (e) => {
      if (e.target.matches('input, textarea, select')) return;
      if (e.key === 'f' || e.key === 'F') this.map.reframe();

      // The map control owns its own label, so the shortcut just toggles.
      if (e.key === 't' || e.key === 'T') this.map.toggleTilt();

      // The map control owns its own state, so the shortcut just toggles.
      if (e.key === 'c' || e.key === 'C') this.map.toggleCurtain();

      if (e.key === 'm' || e.key === 'M') {
        this.map.setBasemap(this.map.basemap() === 'map' ? 'satellite' : 'map');
      }
    });

    document.getElementById('clear-library').addEventListener('click', async () => {
      const records = await this.library.list();

      if (records.length === 0) return;

      const confirmed = await confirmAction({
        title: `Remove all ${records.length} flight${records.length === 1 ? '' : 's'}?`,
        body: 'They are stored in this browser only, so there is no copy to restore '
          + 'from. Getting them back means uploading each log again, which counts '
          + 'against your daily allowance.',
        confirmLabel: `Remove all ${records.length}`,
      });

      if (!confirmed) return;

      await this.library.clear();
      await this.refreshLibrary();
      this.status('Library cleared.');
      setTimeout(() => this.status(null), 2400);
    });

    window.addEventListener('resize', () => this.map.resize());
  }

  /**
   * Read the remaining allowance without spending any of it.
   *
   * Cached so the click handler can decide synchronously. An unknown figure
   * means allow: a read failing is not a reason to block someone, and the
   * spending endpoint fails closed anyway.
   */
  async refreshQuota() {
    try {
      const response = await fetch(QUOTA_ENDPOINT, { cache: 'no-store' });
      this.quota = response.ok ? await response.json() : null;
    } catch {
      this.quota = null;
    }

    this.paintQuota();
  }

  allowUpload() {
    if (this.busy) return false;
    if (!this.quota || this.quota.remaining === null) return true;

    if (this.quota.available) return true;

    // Offer the way forward rather than only the refusal. Someone who has hit
    // the limit has used the tool twice and found it worth using, which is
    // exactly the moment an email address is worth asking for.
    if (this.quota.canVerify) {
      this.offerVerification();
      return false;
    }

    this.status(
      this.quota.reason === 'offline'
        ? this.quota.message
        : this.quota.scope === 'global'
          ? 'The viewer has hit its daily limit. Try again tomorrow.'
          : `You have used your ${this.quota.limit} log${this.quota.limit === 1 ? '' : 's'} for today.`,
      false,
      true
    );

    return false;
  }

  /** Whether an analysis can be requested right now. */
  allowAnalysis() {
    if (!this.quota || !this.quota.ai) return true;
    return this.quota.ai.available;
  }

  /**
   * Ask for an email address, and refresh the allowance if it is given.
   *
   * Never blocks: declining leaves the tool exactly as it was, with the
   * library and every stored flight still readable.
   */
  async offerVerification() {
    if (this._verifying) return;

    this._verifying = true;

    try {
      const verified = await requestVerification({
        used: this.quota.used || 0,
        limit: this.quota.limit,
        verifiedLimit: this.quota.verifiedLimit || this.quota.limit,

        send: async (email, turnstileToken) => {
          const response = await fetch(VERIFY_SEND_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, turnstileToken: turnstileToken || '' }),
          });

          const payload = await response.json().catch(() => null);

          if (!response.ok) {
            throw new Error((payload && payload.error) || 'We could not send that code.');
          }
        },

        confirm: async (email, code, marketing) => {
          const response = await fetch(VERIFY_CONFIRM_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, code, marketing: marketing === true }),
          });

          const payload = await response.json().catch(() => null);

          if (!response.ok) {
            throw new Error((payload && payload.error) || 'That code was not accepted.');
          }
        },
      });

      if (!verified) return;

      await this.refreshQuota();

      const left = this.quota && this.quota.remaining;
      this.status(
        left ? `Verified. ${left} logs left today.` : 'Verified.'
      );
      setTimeout(() => this.status(null), 3000);
    } finally {
      this._verifying = false;
    }
  }

  /** Reflect the allowance in the dropzone itself, not just on refusal. */
  paintQuota() {
    const zone = this.el.dropzone;
    const note = document.getElementById('quota-note');

    const exhausted = this.quota && this.quota.remaining !== null && !this.quota.available;
    zone.classList.toggle('exhausted', Boolean(exhausted));
    zone.setAttribute('aria-disabled', String(Boolean(exhausted || this.busy)));

    if (!note) return;

    // The button label follows the note: an exhausted dropzone that still
    // says "Choose a log" invites a click that cannot do anything.
    const button = zone.querySelector('button');
    const canVerify = Boolean(this.quota && this.quota.canVerify);

    if (!this.quota || this.quota.remaining === null) {
      note.textContent = '';
    } else if (exhausted) {
      const extra = Math.max(
        0,
        (this.quota.verifiedLimit || 0) - (this.quota.used || 0)
      );

      note.textContent = this.quota.scope === 'global'
        ? 'Daily limit reached. Try again tomorrow.'
        : canVerify && extra > 0
          ? `Get a code to unlock ${extra} more today`
          : 'You have used your allowance for today.';
    } else {
      note.textContent =
        `${this.quota.used || 0} of ${this.quota.limit} used today, `
        + `${this.quota.remaining} left`;
    }

    // Into the label span, never the button: setting textContent on the button
    // deletes its icon, which is how the upload icon kept vanishing a moment
    // after the page settled.
    const uploadLabel = zone.querySelector('.upload-label');

    if (uploadLabel && !this.busy) {
      uploadLabel.textContent = exhausted && canVerify ? 'Get code' : 'Choose a log';
    }

    this.paintAnalysisButton();
  }

  /**
   * The analysis panel's button follows the AI allowance the way the upload
   * button follows the upload one: disabled when it cannot run, with the reason
   * on hover, rather than clickable and then refused.
   */
  paintAnalysisButton() {
    const button = this.el.analysis && this.el.analysis.querySelector('.retry-analysis');
    if (!button) return;

    const allowed = this.allowAnalysis();
    button.disabled = !allowed;

    if (allowed) {
      button.removeAttribute('title');
    } else {
      const ai = this.quota.ai;
      button.title = ai.enabled
        ? `You have used your ${ai.limit} analys${ai.limit === 1 ? 'is' : 'es'} for today.`
        : 'Written analysis is not enabled for this tier.';
    }
  }

  /** Lock the upload controls while a log is in flight. */
  setBusy(busy) {
    this.busy = busy;
    this.el.dropzone.classList.toggle('busy', busy);
    this.el.dropzone.setAttribute('aria-busy', String(busy));

    const button = this.el.dropzone.querySelector('button');
    if (button) button.disabled = busy;

    this.paintQuota();
  }

  /* ------------------------------------------------------------ upload */

  async handleFile(file) {
    this.setBusy(true);

    // Start the MapLibre download now rather than after the parse. Hashing and
    // parsing take a couple of seconds, which is ample cover for a 206 KB
    // fetch, so the map is normally ready by the time the flight opens.
    warmMapLibre();

    try {
      this.status('Reading file…', true);

      // Hash first. A file already in the library costs no request, no quota
      // and no money, so check before doing anything else.
      const hash = await hashFile(file);

      if (await this.library.hasPayload(hash)) {
        this.status('Already in your library, opening…', true);
        // openFlight returns false when the cached payload was incompatible and
        // has been evicted; fall through to parse the log again in that case.
        if (await this.openFlight(hash)) {
          return;
        }
      }

      this.status('Processing flight log…', true);

      // Human check clears BEFORE processing starts and BEFORE the server is
      // touched. A cached log returned above, so it is never gated. If the
      // token cannot be obtained we stop here rather than starting an upload
      // that the server would only reject, which is what made the check appear
      // mid-process.
      const token = await this.ensureHumanToken();
      if (token === null) {
        return;
      }

      const form = new FormData();
      form.append('file', file, file.name);
      form.append('cf-turnstile-response', token);

      let response;
      try {
        response = await fetch(PROCESS_ENDPOINT, { method: 'POST', body: form });
      } finally {
        turnstileGate.reset();
      }

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        this.handleProcessError(response.status, payload);
        return;
      }

      if (!payload || payload.version !== 1) {
        throw new Error('The parser returned an unreadable response. Please try again.');
      }

      const flight = new Flight(payload);
      const bytes = await this.library.save(hash, payload, flight.indexRecord(hash));

      // Keep the raw log (gzipped) so CSV/KML can be exported later, including
      // on return visits. Non-blocking: viewing the flight does not wait on it.
      this.library.saveLog(hash, file).catch((error) => {
        console.warn('Could not store the raw log for export', error);
      });

      await this.refreshLibrary();
      this.status(`Parsed ${flight.count.toLocaleString()} rows · ${fmt.bytes(bytes)} stored locally`);
      setTimeout(() => this.status(null), 2600);

      await this.ensureMap();
      this.show(flight, hash);

      // Deliberately not awaited. The parse takes a couple of seconds and the
      // analysis five to ten, so waiting for it would triple the time before
      // anyone sees their flight, for prose most people read second.
      this.autoAnalyse(hash);
    } catch (error) {
      this.status(error.message || 'Could not process that log.', false, true);
    } finally {
      this.setBusy(false);
      this.refreshQuota();
    }
  }

  /**
   * Resolve a Turnstile token before any processing or upload begins.
   *
   * Returns '' when the check is off (the server accepts that), the token when
   * one is available, or null when it could not be obtained — in which case
   * the caller stops and the person has already been told why. With
   * interaction-only the widget is usually pre-armed, so the common path
   * returns instantly; a token is only waited for when a challenge is shown.
   */
  async ensureHumanToken() {
    if (!turnstileGate.enabled()) {
      return '';
    }

    // The dropzone normally starts this on first contact. An upload arriving by
    // some other route would otherwise sit out the whole timeout waiting for a
    // widget nobody had asked to load.
    await startTurnstile();

    const armed = turnstileGate.peek();
    if (armed) {
      return armed;
    }

    // No token yet. With interaction-only this is where a challenge may
    // appear; give the person time to complete it rather than starting an
    // upload the server would only reject.
    this.status('Verifying you\u2019re human…', true);
    const token = await turnstileGate.token(30000);

    if (token) {
      return token;
    }

    this.status('Please complete the verification, then choose your log again.', false, true);
    return null;
  }

  handleProcessError(status, payload) {
    const message = (payload && payload.error) || 'Could not process that log.';

    if (status === 429 && payload && payload.canVerify) {
      this.status(`${message} Verify your email to continue.`, false, true);
      return;
    }

    this.status(message, false, true);
  }

  /* ------------------------------------------------------------ library */

  async refreshLibrary() {
    const records = await this.library.list();
    const list = this.el.libraryList;

    if (records.length === 0) {
      list.innerHTML = '<p class="empty">Nothing here yet. Your parsed flights will appear in this list.</p>';
      this.el.usage.innerHTML = '';
      this.el.landing.classList.remove('has-flights');
      return;
    }

    list.innerHTML = records
      .map((r) => {
        const title = escapeHtml(r.aircraftName || r.aircraftModel || 'Unknown aircraft');
        const stamp = r.flownAt || r.startEpoch;
        const when = stamp ? fmt.clock(stamp, true, r.timeZone) : 'Date unknown';
        const detail = [
          fmt.duration(r.durationSeconds),
          units.distance(r.distanceTotalM),
          r.batteryPercentStart !== null && r.batteryPercentStart !== undefined
            ? `${r.batteryPercentStart}% → ${r.batteryPercentEnd}%`
            : null,
        ].filter(Boolean).join(' · ');

        const flags = warningBadge(r);

        return `
          <div class="library-item ${r.payloadEvicted ? 'evicted' : ''}" data-hash="${r.hash}" role="button" tabindex="0">
            <div class="thumb">${thumbnail(r.thumbnailPath)}</div>
            <div class="library-body">
              <div class="title">${when}<span class="model">${title}</span></div>
              <div class="filename">${escapeHtml(r.filename || '')}</div>
              <div class="sub">${r.payloadEvicted ? 'Cleared to save space, upload again to view' : detail + flags}</div>
            </div>
            <button type="button" class="remove" data-remove="${r.hash}" aria-label="Remove this flight">✕</button>
          </div>`;
      })
      .join('');

    list.querySelectorAll('.library-item').forEach((item) => {
      const open = () => {
        if (item.classList.contains('evicted')) return;
        this.openFlight(item.dataset.hash);
      };
      item.addEventListener('click', (e) => {
        if (e.target.closest('[data-remove]')) return;
        open();
      });
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
      });
    });

    list.querySelectorAll('[data-remove]').forEach((button) => {
      button.addEventListener('click', async (e) => {
        e.stopPropagation();

        const record = records.find((r) => r.hash === button.dataset.remove);

        // Name the flight rather than asking about "this item": with a list of
        // near-identical DJI filenames, the row you clicked and the row you
        // meant are easy to confuse, and the dialog is the last chance to
        // notice.
        // Aircraft and time, the same pair the row shows. Naming only the
        // timestamp makes the reader match a date against a list they can no
        // longer see, and it is the aircraft they recognise first.
        const label = record
          ? [record.aircraftName || record.aircraftModel,
             fmt.clock(record.flownAt || record.startEpoch, true, record.timeZone)]
              .filter(Boolean).join(', ')
          : 'This flight';

        const confirmed = await confirmAction({
          title: 'Remove this flight?',
          body: `${label} will be removed from this browser. Getting it back means `
            + 'uploading the log again, which counts against your daily allowance.',
          confirmLabel: 'Remove',
        });

        if (!confirmed) return;

        await this.library.remove(button.dataset.remove);
        await this.refreshLibrary();
      });
    });

    this.el.landing.classList.add('has-flights');

    const usage = await this.library.usage();
    this.el.usage.innerHTML = `
      <span>${usage.count} flight${usage.count === 1 ? '' : 's'} · ${fmt.bytes(usage.totalBytes)}</span>
      <span>stored in this browser only</span>`;
  }

  async openFlight(hash) {
    const contract = await this.library.load(hash);

    if (!contract) {
      this.status('That flight is no longer stored. Upload the log again.', false, true);
      return false;
    }

    let flight;
    try {
      flight = new Flight(contract);
    } catch (error) {
      // A payload cached by an older parser or a broken parse is incompatible,
      // not fatal. Drop it so the list stays clean and the caller can re-parse
      // rather than the whole view crashing on it.
      await this.library.remove(hash);
      await this.refreshLibrary();
      return false;
    }

    await this.ensureMap();
    this.show(flight, hash);
    this.status(null);
    return true;
  }

  /* ------------------------------------------------------------- viewer */

  show(flight, hash) {
    this.flight = flight;
    this.hash = hash;

    // Debug handle: lets the current flight's contract be inspected from the
    // console (e.g. window.ddFlight.cols.homeDistanceM). Harmless, read-only.
    window.ddFlight = flight;

    this.el.landing.hidden = true;
    this.el.viewer.hidden = false;
    this.el.app.classList.remove('no-flight');
    this.setHeaderControls(true);

    // When, then what, then which file. The date is the thing someone is
    // actually looking for when they have a folder of these open.
    const flown = fmt.clock(flight.meta.flownAt, true, flight.meta.timeZone);
    const approximate = flight.meta.flownAtSource === 'parsed';

    this.el.headerFile.innerHTML = `
      <span class="name"${approximate ? ' title="No date in the log or its filename, so this is when it was processed"' : ''}>
        ${flown}${approximate ? ' (processed)' : ''}
      </span>
      <span class="aircraft">${escapeHtml(flight.label())}</span>
      <span class="filename">${escapeHtml(flight.meta.filename || '')}</span>`;

    this.destroyComponents();
    this._readout = null;

    this.components.profile = new FlightProfile(
      this.el.profile,
      flight,
      // The fraction is only supplied during playback, and only the table
      // uses it: everything else works in whole rows.
      (index, fraction) => this.setIndex(index, fraction)
    );

    // The battery strip is slaved to the profile: seeking on it routes through
    // the profile so playback state stays in one place, and its marker is moved
    // by setIndex like everything else.
    this.components.battery = new BatteryChart(
      this.el.batteryProfile,
      flight,
      (index) => { this.components.profile.pause(); this.components.profile.seek(index); }
    );
    const slave = (index) => { this.components.profile.pause(); this.components.profile.seek(index); };
    if (this.el.signalProfile) {
      this.components.signal = new SignalChart(this.el.signalProfile, flight, slave);
    }
    if (this.el.attitudeProfile) {
      this.components.attitude = new AttitudeChart(this.el.attitudeProfile, flight, slave);
    }
    this.mountStripToggle();
    this.components.table = new TelemetryTable(this.el.table, flight, (i) => {
      this.components.profile.pause();
      this.components.profile.seek(i);
    });
    this.components.gimbals = new Gimbals(this.el.gimbals, flight);
    if (this.el.attitudeModel) {
      this.components.attitudeModel = new AttitudeModel(this.el.attitudeModel, flight);
    }
    this.components.toasts = new Toasts(this.el.toasts, (t) => fmt.elapsed(t));

    // Chase the aircraft only while playing. Scrubbing by hand should leave
    // the camera where the user put it, which is why the manual Follow toggle
    // was never doing anything anyone wanted.
    this.components.profile.onPlayStateChange = (playing) => {
      this.map.setFollow(playing);

      // Pressing play is a request to watch the flight, which means the table
      // should come along. Scrolling it by hand switches following off, and
      // without this that decision outlived the scroll: every later playback
      // ran with the table parked wherever it had been left.
      if (playing) this.components.table.follow();
    };

    const placed = this.map.setFlight(flight);
    document.getElementById('map-fallback').hidden = placed;

    if (!placed && !flight.quality.hasPosition) {
      document.getElementById('map-fallback').innerHTML = `
        <strong>No position data in this log</strong>
        <span>Indoor and ATTI flights record no GPS. The timeline and table below still work.</span>`;
    }

    this.renderSidebar();

    // The sidebar keeps its own scroll position, and nothing was resetting it.
    // Open a flight, scroll down to the warnings, close it, open another, and
    // the panel is still scrolled: the summary at the top is simply not where
    // you left your eyes.
    this.el.sidebar.scrollTop = 0;
    this.paintQuota();



    this.setIndex(0);
    requestAnimationFrame(() => this.map.resize());
  }

  closeFlight() {
    this.destroyComponents();
    this.map.clear();
    this.flight = null;
    this.hash = null;
    this.el.viewer.hidden = true;
    this.el.landing.hidden = false;
    this.el.app.classList.add('no-flight');
    this.el.headerFile.innerHTML = '';
    this.setHeaderControls(false);
    this.refreshLibrary();
  }

  /** Controls that only mean anything while a flight is open. */
  setHeaderControls(visible) {
    // The analyse button is deliberately not here: analysis runs on its own in
    // the panel, and its only affordance (a retry on failure) lives there too,
    // so there is no top-bar button to show or hide.
    for (const id of ['download-pdf', 'download-csv', 'download-kml', 'unit-toggle', 'close-flight']) {
      const button = document.getElementById(id);
      if (button) button.hidden = !visible;
    }
  }

  /**
   * Drag handles on the map's right and bottom edges resize the sidebar width
   * and the map height. The sizes are CSS variables on the grid and persist in
   * localStorage, so a chosen layout survives reloads. The map is told to resize
   * as it goes so the tiles keep up.
   */
  initResizers() {
    const app = this.el.app;
    if (!app) return;

    try {
      const w = localStorage.getItem('ddlv-sidebar-w');
      const h = localStorage.getItem('ddlv-map-h');
      if (w) app.style.setProperty('--sidebar-w', w);
      if (h) app.style.setProperty('--map-h', h);
    } catch (e) { /* private mode: fall back to defaults */ }

    const stage = document.querySelector('.stage');
    const nudgeMap = () => { if (this.map && this.map.resize) this.map.resize(); };

    document.querySelectorAll('.resize-handle').forEach((handle) => {
      const kind = handle.dataset.resize;

      handle.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        handle.setPointerCapture(event.pointerId);
        handle.classList.add('dragging');

        const move = (ev) => {
          if (kind === 'sidebar') {
            const max = Math.min(680, window.innerWidth * 0.6);
            const width = Math.min(max, Math.max(280, window.innerWidth - ev.clientX));
            app.style.setProperty('--sidebar-w', `${Math.round(width)}px`);
          } else {
            const top = stage ? stage.getBoundingClientRect().top : 52;
            const height = Math.min(window.innerHeight - 300, Math.max(200, ev.clientY - top));
            app.style.setProperty('--map-h', `${Math.round(height)}px`);
          }
          nudgeMap();
        };

        const up = () => {
          handle.classList.remove('dragging');
          handle.removeEventListener('pointermove', move);
          handle.removeEventListener('pointerup', up);
          handle.removeEventListener('pointercancel', up);
          try {
            localStorage.setItem('ddlv-sidebar-w', app.style.getPropertyValue('--sidebar-w'));
            localStorage.setItem('ddlv-map-h', app.style.getPropertyValue('--map-h'));
          } catch (e) { /* private mode: not persisted */ }
          nudgeMap();
        };

        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', up);
        handle.addEventListener('pointercancel', up);
      });
    });
  }

  destroyComponents() {
    for (const key of Object.keys(this.components)) {
      if (this.components[key] && this.components[key].destroy) this.components[key].destroy();
    }
    this.components = {};
  }

  setIndex(index, fraction = undefined) {
    if (!this.flight) return;

    const row = this.flight.row(index);
    if (!row) return;

    this.map.setIndex(index);
    this.components.table.setCurrent(index, fraction);
    this.components.gimbals.update(index);
    if (this.components.attitudeModel) this.components.attitudeModel.setIndex(index);
    if (this.components.battery) this.components.battery.setIndex(index);
    if (this.components.signal) this.components.signal.setIndex(index);
    if (this.components.attitude) this.components.attitude.setIndex(index);
    if (this.components.profile && this.components.profile.updateValues) this.components.profile.updateValues();

    this.paintReadouts(row);
    this.renderToasts(index);
  }

  /**
   * The chip row under the profile: which secondary strips are shown.
   *
   * The profile is the transport and is always visible. Battery, signal and
   * attitude are optional strips; each chip shows or hides one, any combination
   * can be up at once, and the choice persists so it is not remade on every
   * flight. A strip whose data this log does not carry is dropped from the row
   * rather than offered as a dead toggle, and its (empty) container stays
   * hidden regardless of the stored preference.
   */
  mountStripToggle() {
    const row = this.el.stripToggle;
    if (!row) return;

    const all = [
      { id: 'battery', label: 'Battery', el: this.el.batteryProfile, avail: !!this.components.battery },
      { id: 'signal', label: 'Signal', el: this.el.signalProfile, avail: !!this.components.signal && this.flightHasSignal() },
      { id: 'attitude', label: 'Attitude', el: this.el.attitudeProfile, avail: !!this.components.attitude && this.flightHasAttitude() },
    ];
    const chips = all.filter((s) => s.el && s.avail);
    const shown = this.stripVisibility(chips);

    const eye = (on) => (on ? '<i class="fa-light fa-eye"></i>' : '<i class="fa-light fa-eye-slash"></i>');
    row.innerHTML = chips
      .map(
        (s) => `<button type="button" class="strip-chip ${shown.has(s.id) ? 'on' : ''}"
            data-strip="${s.id}" aria-pressed="${shown.has(s.id)}">${eye(shown.has(s.id))}<span>${s.label}</span></button>`
      )
      .join('');
    row.hidden = chips.length === 0;

    const apply = () => {
      for (const s of all) {
        if (s.el) s.el.classList.toggle('strip-off', !(s.avail && shown.has(s.id)));
      }
    };
    apply();

    row.querySelectorAll('[data-strip]').forEach((chip) => {
      chip.addEventListener('click', () => {
        const id = chip.dataset.strip;
        if (shown.has(id)) shown.delete(id); else shown.add(id);
        const on = shown.has(id);
        chip.classList.toggle('on', on);
        chip.setAttribute('aria-pressed', String(on));
        const icon = chip.querySelector('i');
        if (icon) icon.className = on ? 'fa-light fa-eye' : 'fa-light fa-eye-slash';
        try { localStorage.setItem('ddlv-strips', JSON.stringify([...shown])); } catch { /* preference only */ }
        apply();
      });
    });
  }

  /**
   * The stored set of visible strips, intersected with what this flight has.
   * Default when nothing is stored: battery on (it used to be always shown),
   * the new strips off, so an existing user is not handed three strips at once.
   */
  stripVisibility(chips) {
    const ids = new Set(chips.map((s) => s.id));
    try {
      const stored = localStorage.getItem('ddlv-strips');
      if (stored) {
        return new Set(JSON.parse(stored).filter((id) => ids.has(id)));
      }
    } catch { /* storage may be unavailable */ }
    return new Set(ids.has('battery') ? ['battery'] : []);
  }

  flightHasSignal() {
    return !!this.flight && (this.flight.has('uplinkSignalPct') || this.flight.has('downlinkSignalPct') || this.flight.has('satellites'));
  }

  flightHasAttitude() {
    return !!this.flight && (this.flight.has('pitchDeg') || this.flight.has('rollDeg') || this.flight.has('yawDeg'));
  }

  /**
   * Build the readout panel once.
   *
   * Rewriting innerHTML on every frame destroys and recreates five rows of
   * DOM sixty times a second. That is the flicker: the browser has to reparse,
   * relayout and repaint a panel whose text is the only thing that changed.
   */
  buildReadouts() {
    // The unit lives in the label, not beside the number. That way every value
    // is a bare figure sharing one right-hand edge, and the column can be
    // scanned down without the eye stepping over "m", "m/s" and "%".
    const fields = [
      ['height', 'Height', 'altitude'],
      ['speed', 'Speed', 'speed'],
      ['home', 'Home', 'distance'],
      ['battery', 'Battery', 'percent'],
      ['sats', 'Sats', null],
    ];

    this.el.readouts.innerHTML =
      fields
        .map(
          ([key, label, kind]) => `<div class="readout">
            <span class="panel-label" data-l="${key}">${label}${unitSuffix(kind)}</span>
            <span class="readout-value" data-v="${key}">—</span>
          </div>`
        )
        .join('') +
      `<div class="wind empty" data-wind>
        <svg class="wind-rose" viewBox="0 0 38 38" role="img" aria-hidden="true">
          <circle cx="19" cy="19" r="15" fill="none" stroke="var(--rule)" stroke-width="1"/>
          <path d="M19 1.5 L22 5.5 L16 5.5 Z" fill="var(--text-dim)"/>
          <line x1="34" y1="19" x2="31" y2="19" stroke="var(--rule)" stroke-width="1"/>
          <line x1="19" y1="34" x2="19" y2="31" stroke="var(--rule)" stroke-width="1"/>
          <line x1="4" y1="19" x2="7" y2="19" stroke="var(--rule)" stroke-width="1"/>
          <g data-needle style="transform-origin:19px 19px">
            <path d="M19 8 L24.5 30 L19 25.5 L13.5 30 Z" fill="var(--trace)"/>
          </g>
        </svg>
        <span class="wind-figures">
          <span class="wind-speed" data-wind-speed>—</span>
          <span class="wind-from" data-wind-from>—</span>
        </span>
      </div>`;

    this._readout = {};

    for (const [key] of fields) {
      this._readout[key] = {
        value: this.el.readouts.querySelector(`[data-v="${key}"]`),
        label: this.el.readouts.querySelector(`[data-l="${key}"]`),
      };
    }

    this._wind = {
      root: this.el.readouts.querySelector('[data-wind]'),
      needle: this.el.readouts.querySelector('[data-needle]'),
      speed: this.el.readouts.querySelector('[data-wind-speed]'),
      from: this.el.readouts.querySelector('[data-wind-from]'),
    };
  }

  paintReadouts(row) {
    if (!this._readout) this.buildReadouts();

    const values = {
      height: bare(units.altitude(row.heightM)),
      speed: bare(units.speed(row.speedHorizontalMs)),
      home: bare(units.distance(row.homeDistanceM)),
      // The lower of the two on a dual-battery airframe: the flight ends when
      // the first pack runs out, whatever the other has left.
      battery: this.lowestBattery(row),
      sats: fmt.number(row.satellites, 0),
    };

    for (const key in values) {
      const target = this._readout[key].value;

      // Assigning identical text still marks the node dirty in some engines,
      // so compare first.
      if (target.textContent !== values[key]) target.textContent = values[key];
    }

    this.paintWind(row);
  }

  /** The lowest reported battery percentage at this instant. */
  lowestBattery(row) {
    const levels = [row.batteryPercent, row.battery2Percent]
      .filter((v) => v !== null && v !== undefined);

    if (levels.length === 0) return '—';

    return String(Math.round(Math.min(...levels)));
  }

  /**
   * The wind rose.
   *
   * North up, true direction, meteorological convention: the arrow sits on the
   * side the wind comes FROM and flies inward at the centre. "Wind 270 at 15"
   * is how a METAR, a TAF and a tower all report it, so it is the frame the
   * reader already has loaded.
   *
   * The arrow points DOWNWIND, the way the air is actually moving, while the
   * label names the direction it comes from. Those agree: wind from the west
   * travels east.
   *
   * So the needle is rotated by the bearing plus 180.
   *
   * The arrow's own bounding box is centred on 19,19, which is also the
   * transform-origin. Get that wrong by even a few units and it swings around
   * a point near its tail rather than spinning on itself.
   */
  paintWind(row) {
    const wind = this._wind;

    if (!wind || row.windSpeedMs === null || row.windSpeedMs === undefined) {
      if (wind) wind.root.classList.add('empty');
      return;
    }

    wind.root.classList.remove('empty');
    wind.speed.textContent = units.speed(row.windSpeedMs, 1);

    const from = row.windFromDeg;

    if (from === null || from === undefined) {
      wind.needle.style.transform = 'none';
      wind.from.textContent = '—';
      return;
    }

    wind.needle.style.transform = `rotate(${(from + 180) % 360}deg)`;
    wind.from.textContent = `from ${compassPoint(from)} ${Math.round(from)}\u00B0`;
  }

  /**
   * Raise a toast  /**
   * Raise a toast for anything that starts at this row.
   *
   * Only the start row, because the parser already collapsed a repeated
   * message into one event spanning many rows. The toast then dwells for a
   * few seconds under its own clock rather than disappearing the moment the
   * playhead moves on.
   */
  renderToasts(index) {
    for (const event of this.flight.eventsAt(index)) {
      this.components.toasts.push(event);
    }
  }

  renderSidebar() {
    const f = this.flight;
    const s = f.summary;
    const b = s.battery;

    const batteryClass = b.percentEnd === null ? '' : b.percentEnd < 15 ? 'warn' : b.percentEnd < 30 ? 'caution' : '';

    this.el.stats.innerHTML = `
      ${stat('Duration', fmt.duration(s.durationSeconds))}
      ${stat('Distance flown', units.distance(s.distanceTotalM))}
      ${stat('Max speed', units.speed(s.speedHorizontalMaxMs))}
      ${stat('Max height AGL', units.altitude(s.heightMaxM, 0))}
      ${stat('Max altitude AMSL', units.altitude(s.altitudeMaxM, 0))}
      ${stat('Max from home', units.distance(s.homeDistanceMaxM))}`;

    const used = b.percentStart !== null && b.percentEnd !== null ? b.percentStart - b.percentEnd : null;

    this.el.details.innerHTML = `
      ${this.equipmentMarkup()}
      ${this.batteryMarkup()}

      ${sectionHead('Position', 'margin-top:16px')}
      <dl class="rows">
        ${kv('Satellites', `${s.gps.satellitesMin} – ${s.gps.satellitesMax}`)}
        ${kv('Mean GPS level', fmt.number(s.gps.levelMean, 1))}
        ${kv('Take-off', s.takeoff ? fmt.coordinate(s.takeoff.lat, s.takeoff.lon) : 'Not recorded')}
        ${kv('Home', s.home ? fmt.coordinate(s.home.lat, s.home.lon) : 'Not recorded')}
        ${kv('Max distance from home', s.homeDistanceMaxM != null ? units.distance(s.homeDistanceMaxM) : 'Not recorded')}
        ${s.rth && s.rth.altitudeM != null ? kv('RTH altitude', units.altitude(s.rth.altitudeM, 0)) : ''}
        ${s.rth && s.rth.mode ? kv('RTH mode', s.rth.mode) : ''}
        ${s.rth && s.rth.heightLimitM != null ? kv('Height limit', units.altitude(s.rth.heightLimitM, 0)) : ''}
      </dl>

      ${this.weatherMarkup()}
      ${this.captureMarkup()}`;

    this.renderWarnings();
    this.loadCachedAnalysis();
  }

  /**
   * One block per battery that reported.
   *
   * A dual-battery airframe gets two, headed and numbered. Showing one of a
   * pair is worse than showing neither: it looks complete and is not, and the
   * pack it omits is as likely as the other to be the one that ended the
   * flight.
   */
  batteryMarkup() {
    const batteries = this.flight.summary.batteries || [];

    if (batteries.length === 0) return '';

    const multiple = batteries.length > 1;

    // How long the motors turned, which is what actually drained the pack.
    // Only on the first block: it is a property of the flight, not of one
    // battery, and repeating it under both would imply otherwise.
    const motorSeconds = (this.flight.summary.capture || {}).motorSeconds;

    return batteries
      .map((b, position) => {
        const heading = multiple ? `Battery ${b.number}` : 'Battery';
        const motorRunning = motorSeconds && position === 0
          ? kv('Motors running', fmt.duration(motorSeconds))
          : '';
        const level = b.percentEnd === null ? '' :
          b.percentEnd < 15 ? 'warn' : b.percentEnd < 30 ? 'caution' : '';
        const used = b.percentStart !== null && b.percentEnd !== null
          ? b.percentStart - b.percentEnd
          : null;

        return `
          ${sectionHead(heading, 'margin-top:16px')}
          ${used === null ? '' : `
            <div class="battery-bar">
              <div class="battery-missing" style="width:${100 - b.percentStart}%"></div>
              <div class="battery-consumed" style="width:${used}%"></div>
              <div class="battery-left ${level}" style="width:${b.percentEnd}%"></div>
            </div>
            <div class="battery-key">
              <span class="k-start">${b.percentStart}% at start</span>
              <span class="k-used">${used}% used</span>
              <span class="k-left ${level}">${b.percentEnd}% left</span>
            </div>`}
          <dl class="rows">
            ${kv('Charge', b.percentStart === null ? '—' : `${b.percentStart}% → ${b.percentEnd}%`, level)}
            ${motorRunning}
            ${kv('Consumed', b.capacityUsedMah === null ? '—' : `${b.capacityUsedMah} mAh`)}
            ${kv('Voltage', b.voltageMinV === null ? '—' : `${b.voltageMinV} – ${b.voltageMaxV} V`)}
            ${b.cellVoltagesEstimated
              ? kv('Cell deviation', '<span title="The aircraft reported an average rather than per-cell measurements, so a deviation figure derived from it means nothing.">Not measured</span>')
              : kv('Cell deviation', b.cellDeviationMaxV === null ? '—' : `${b.cellDeviationMaxV} V`,
                   b.cellDeviationMaxV > 0.1 ? 'caution' : '')}
            ${kv('Temperature', b.temperatureMaxC === null ? '—' :
                 `${units.temperature(b.temperatureMinC)} – ${units.temperature(b.temperatureMaxC)}`)}
            ${kv('Peak draw', b.currentMaxA === null ? '—' : `${b.currentMaxA} A`)}
            ${kv('Charge cycles', b.cycleCount === null ? '—' : b.cycleCount)}
            ${b.fullCapacityMah === null ? '' : kv('Full capacity', `${b.fullCapacityMah} mAh`)}
          </dl>`;
      })
      .join('');
  }

  /**
   * Wind, when the log recorded any.
   *
   * The rose over the map shows the wind at one instant; this is the flight
   * as a whole, which is the version that belongs in a record and the one the
   * report already carried.
   */
  weatherMarkup() {
    const w = this.flight.summary.weather;
    if (!w) return '';

    const rows = [];
    const round1 = (n) => Math.round(n * 10) / 10;

    const cond = weatherCodeText(w.weatherCode);
    if (cond) {
      const iconUrl = weatherIconUrl(w.weatherCode, w.isDay);
      const icon = iconUrl ? `<img class="wx-icon" src="${iconUrl}" alt="" aria-hidden="true"> ` : '';
      rows.push(kv('Conditions', `${icon}${cond}`));
    }
    if (w.temperatureC !== null && w.temperatureC !== undefined) rows.push(kv('Temperature', units.temperature(w.temperatureC)));
    if (w.dewpointC !== null && w.dewpointC !== undefined) rows.push(kv('Dew point', units.temperature(w.dewpointC)));

    // Wind at each level the log's altitude band spans; gust gets its own row.
    for (const level of ['10', '80', '120']) {
      const wl = w.wind && w.wind[level];
      if (!wl || wl.speedMs === null || wl.speedMs === undefined) continue;
      const dir = (wl.fromDeg !== null && wl.fromDeg !== undefined)
        ? ` from ${compassPoint(wl.fromDeg)} ${Math.round(wl.fromDeg)}\u00B0`
        : '';
      rows.push(kv(`Wind ${level} m`, `${units.speed(wl.speedMs)}${dir}`));
      if (level === '10' && wl.gustMs !== null && wl.gustMs !== undefined) {
        rows.push(kv('Gusts (10 m)', units.speed(wl.gustMs)));
      }
    }

    if (w.cloudCoverPct !== null && w.cloudCoverPct !== undefined) rows.push(kv('Cloud cover', `${Math.round(w.cloudCoverPct)}%`));
    if (w.visibilityM !== null && w.visibilityM !== undefined) rows.push(kv('Visibility', units.distance(w.visibilityM)));
    if (w.rainMm !== null && w.rainMm !== undefined) rows.push(kv('Rain', `${round1(w.rainMm)} mm`));
    if (w.freezingLevelM !== null && w.freezingLevelM !== undefined) rows.push(kv('Freezing level', units.altitude(w.freezingLevelM)));
    if (w.surfacePressureHpa !== null && w.surfacePressureHpa !== undefined) rows.push(kv('Pressure', `${Math.round(w.surfacePressureHpa)} hPa`));

    if (w.kp && w.kp.value !== null && w.kp.value !== undefined) {
      rows.push(kv('Kp index', `${round1(w.kp.value)} \u00B7 ${kpLabel(w.kp.value)}`));
    }

    if (rows.length === 0) return '';

    return `
      ${sectionHead('Weather', 'margin-top:16px')}
      <dl class="rows">${rows.join('')}</dl>`;
  }

  /** Photos and video, when the log recorded any. */
  captureMarkup() {
    const capture = this.flight.summary.capture || {};
    const rows = [];

    if (capture.photos) rows.push(kv('Photos', capture.photos));
    if (capture.videoSeconds) rows.push(kv('Video', fmt.duration(capture.videoSeconds)));

    if (rows.length === 0) return '';

    return `
      ${sectionHead('Capture', 'margin-top:16px')}
      <dl class="rows">${rows.join('')}</dl>`;
  }

  /**
   * Serials, firmware and model names.
   *
   * Only groups the log actually carried. A DJI log's equipment block varies
   * with the aircraft, the app and how it was exported, and an "Controller"
   * heading with nothing beneath it reads as a fault rather than as an absence.
   */
  equipmentMarkup() {
    const devices = this.flight.meta.devices || {};

    const groups = [
      ['aircraft', 'Aircraft'],
      ['flightController', 'Flight controller'],
      ['controller', 'Remote'],
      ['battery', 'Battery'],
      ['battery2', 'Battery 2'],
      ['camera', 'Camera'],
      ['camera2', 'Camera 2'],
      ['gimbal', 'Gimbal'],
      ['gimbal2', 'Gimbal 2'],
      ['ssd', 'SSD'],
      ['app', 'App'],
    ];

    const labels = {
      model: 'model',
      manufacturer: 'maker',
      // "name" is the nickname the pilot gave the aircraft, which is a
      // different thing from its model and was previously shown as one.
      name: 'name',
      serial: 'serial',
      firmware: 'firmware',
      version: 'version',
      platform: 'platform',
    };

    const rows = groups
      .filter(([key]) => devices[key])
      .flatMap(([key, heading]) =>
        Object.entries(devices[key]).map(([field, value]) =>
          kv(
            `${heading} ${labels[field] || field}`,
            `<span class="serial">${escapeHtml(value)}</span>`
          )
        )
      );

    if (rows.length === 0) return '';

    return `
      ${sectionHead('Equipment', 'margin-top:16px')}
      <dl class="rows">${rows.join('')}</dl>`;
  }

  /**
   * Every warning and critical event, in order, each one a jump to its moment.
   *
   * The map and the scrubber show what happened; this answers "what should I
   * look at", which is the question someone opens a log with.
   */
  renderWarnings() {
    const events = this.flight
      .timelineEvents()
      .filter((e) => e.severity === 'warning' || e.severity === 'critical');

    // The count belongs in the heading, where it can be read without scrolling
    // the list. Absent at zero rather than showing "0": a clean flight should
    // not carry a number that only ever means nothing happened.
    const counter = document.getElementById('warning-count');

	if (counter) {
      const critical = events.filter((e) => e.severity === 'critical').length;
      const warning = events.filter((e) => e.severity === 'warning').length;
      const parts = [];
      if (critical) parts.push(`<span class="count-critical">${critical}</span>`);
      if (warning) parts.push(`<span class="count-warning">${warning}</span>`);
      counter.innerHTML = parts.join('');
    }
    if (events.length === 0) {
      this.el.warnings.innerHTML = '<p class="event-empty">No warnings recorded.</p>';
      return;
    }

    this.el.warnings.innerHTML = events
      .map(
        (e) => `<button type="button" class="event ${e.severity}" data-index="${e.index}">
          <span class="at">${fmt.elapsed(e.timeS)}</span>
          <span class="what">${escapeHtml(e.text)}${
            e.repeatCount > 1 ? ` <span class="count">×${e.repeatCount}</span>` : ''
          }</span>
        </button>`
      )
      .join('');

    this.el.warnings.querySelectorAll('.event').forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.index);
        this.components.profile.pause();
        this.components.profile.seek(index);

        // Put the row at the top rather than wherever the follow logic would
        // have left it. Jumping to a warning is a request to read forward from
        // it, and the default centring buried it at the bottom edge.
        this.components.table.revealAtTop(index);
      });
    });
  }

  setButtonBusy(button, busy) {
    if (!button) return;
    const icon = button.querySelector('i');

    if (busy) {
      if (icon) {
        button.dataset.icon = icon.className;
        icon.className = 'fa-regular fa-arrows-rotate fa-spin';
      }
      button.disabled = true;
    } else {
      if (icon && button.dataset.icon) {
        icon.className = button.dataset.icon;
        delete button.dataset.icon;
      }
      button.disabled = false;
    }
  }

  /**
   * Download the full log as CSV or KML. The parser is stateless, so the raw
   * log (held gzipped in the library) is re-sent with the keychain from the
   * original parse; export.php forwards it to the parser and streams the file
   * back. Works on return visits because the log was stored at upload.
   */
  async exportLog(format) {
    if (!this.flight || !this.hash) return;

    const label = format.toUpperCase();
	const button = document.getElementById(`download-${format}`);
    this.setButtonBusy(button, true);

    try {
      const log = await this.library.loadLog(this.hash);

      if (!log || !log.blob) {
        this.status(
          `The original log is no longer stored for this flight, so ${label} export is not available. Re-upload the log to download it.`,
          false,
          true
        );
        return;
      }

      this.status(`Preparing ${label}…`, true);

      const form = new FormData();
      form.append('log', log.blob, log.gzip ? 'log.txt.gz' : 'log.txt');
      form.append('format', format);

      const keychain = this.flight.raw && this.flight.raw.keychain;
      if (keychain) form.append('keychain', JSON.stringify(keychain));

      const response = await fetch(EXPORT_ENDPOINT, { method: 'POST', body: form });

      if (!response.ok) {
        // Success is the file; failure is JSON with a reason.
        const detail = await response.json().catch(() => null);
        throw new Error((detail && detail.error) || `The ${label} export could not be produced.`);
      }

      const blob = await response.blob();
      download(exportFilename(this.flight, format), blob, blob.type || 'application/octet-stream');
      this.status(null);
	} catch (error) {
      this.status(error.message || `The ${label} export could not be produced.`, false, true);
    } finally {
      this.setButtonBusy(button, false); 
    }
  }


  /**
   * Build and download the PDF report.
   *
   * The browser sends structured data, not markup. Gotenberg fetches whatever
   * the document it renders references, so the server composes the HTML from a
   * template and nothing user-supplied ever becomes a tag.
   */
  async downloadReport() {
    const button = document.getElementById('download-pdf');

    if (!this.flight || button.disabled) return;

	this.setButtonBusy(button, true);
    this.status('Building report…', true);

    try {
      const analysis = await this.library.loadAnalysis(
        this.hash, this.flight.meta.parserVersion, PROMPT_VERSION
      );

      const payload = {
        meta: this.flight.meta,
        summary: this.flight.summary,
        events: this.flight.timelineEvents(),
        series: this.flight.sample(
          [
            'timeS', 'heightM', 'homeDistanceM', 'speedHorizontalMs',
            'batteryPercent', 'batteryCurrentA', 'batteryVoltageV',
            'battery2Percent', 'battery2CurrentA', 'battery2VoltageV',
            'satellites', 'uplinkSignalPct', 'downlinkSignalPct',
            'pitchDeg', 'rollDeg', 'yawDeg',
          ],
          120
        ),
        analysis: typeof analysis === 'string' ? { text: analysis } : analysis,
        units: units.system,
        mapImage: await this.map.snapshot({ topDown: true }),
      };

      const response = await fetch(REPORT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // Success is a PDF and failure is JSON, so the reason is readable
        // rather than downloaded as a file containing it.
        const detail = await response.json().catch(() => null);
        throw new Error((detail && detail.error) || 'The report could not be produced.');
      }

      const blob = await response.blob();
      download(reportFilename(this.flight), blob);

      this.status(null);
    } catch (error) {
      this.status(error.message || 'The report could not be produced.', false, true);
    } finally {
      this.setButtonBusy(button, false);
      this.refreshQuota();
    }
  }

  /* ------------------------------------------------------------ analysis */

  /**
   * Produce an analysis for a newly uploaded flight, quietly.
   *
   * New uploads only. Opening an older flight that has none would otherwise
   * spend an allowance the reader did not ask to spend, so those keep the
   * button instead.
   */
  async autoAnalyse(hash) {
    if (this.hash !== hash) return;

    const cached = await this.library.loadAnalysis(
      hash, this.flight.meta.parserVersion, PROMPT_VERSION
    );

    if (cached) return;
    if (!this.allowAnalysis()) return;

    await this.analyse({ automatic: true });
  }

  async loadCachedAnalysis() {
    const cached = await this.library.loadAnalysis(this.hash, this.flight.meta.parserVersion, PROMPT_VERSION);

    if (cached) {
      this.renderAnalysis(cached);
      return;
    }

    this.el.analysis.innerHTML =
      '<p class="placeholder">No analysis yet for this flight.</p>' +
      '<button type="button" class="retry-analysis">' +
      '<i class="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i> Generate analysis</button>';

    const run = this.el.analysis.querySelector('.retry-analysis');
    if (run) run.addEventListener('click', () => this.analyse());
    this.paintAnalysisButton();
  }

  /**
   * @param {object} options
   * @param {boolean} options.automatic true when this ran as part of
   *   processing an upload rather than because someone pressed the button
   */
  async analyse(options = {}) {
    const automatic = options.automatic === true;

    // A slow analysis can land after the reader has opened a different flight.
    const hash = this.hash;

    if (!this.allowAnalysis()) {
      const ai = this.quota.ai;

      const message = ai.enabled
        ? `You have used your ${ai.limit} analys${ai.limit === 1 ? 'is' : 'es'} for today.`
        : 'Written analysis is not enabled for this tier.';

      this.el.analysis.innerHTML = `<p class="placeholder">${escapeHtml(message)}</p>`;

      // Only announce it when asked. An automatic step that cannot run has
      // cost nobody anything: the flight is on screen and entirely usable.
      if (!automatic) {
        this.status(message, false, true);
        this.revealAnalysis();
      }

      return;
    }

    // Scrolling the panel into view is right when the button was pressed and
    // rude when it was not: an automatic analysis should not drag the reader
    // away from the map.
    if (!automatic) this.revealAnalysis();

    this.el.analysis.innerHTML =
      '<p class="placeholder"><span class="spinner"></span>Analysing this flight…</p>';

    try {
      // Equipment serials are left out deliberately. Ten component serials
      // next to a take-off coordinate is an identifying pair, and nothing in
      // the analysis is improved by knowing a gimbal serial. The aircraft
      // model does go, because the same figure means different things on a
      // Mini and on an Inspire.
      //
      // The server rebuilds this from a whitelist regardless, so this is the
      // polite version of a rule that is also enforced.
      const digest = {
        meta: {
          aircraftModel: this.flight.meta.aircraftModel,
          aircraftName: this.flight.meta.aircraftName,
          flownAt: this.flight.meta.flownAt,
          batteryCount: this.flight.meta.batteryCount,
        },
        summary: this.flight.summary,
        events: this.flight.events,
        series: this.flight.sample(
          ['timeS', 'heightM', 'speedHorizontalMs', 'batteryPercent', 'satellites'], 60
        ),
      };

      const response = await fetch(ANALYSE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(digest),
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error((payload && payload.error) || 'Analysis is unavailable right now.');
      }

      const analysis = { text: payload.text, notice: payload.notice };

      await this.library.saveAnalysis(
        this.hash, this.flight.meta.parserVersion, PROMPT_VERSION, analysis
      );

      // Rendering a result for a flight that is no longer open would attach
      // one flight's prose to another.
      if (this.hash !== hash) return;

      this.renderAnalysis(analysis);
      if (!automatic) this.status(null);
    } catch (error) {
      if (this.hash !== hash) return;

      // The log parsed and the flight is usable, so a failed analysis is a
      // missing extra rather than a broken upload. Say so where the analysis
      // would have been, and leave the button to try again.
      //
      // The status bar only when asked: an automatic step that fails should
      // not put an error banner over a flight that is working perfectly well.
      this.el.analysis.innerHTML =
        `<p class="placeholder">${escapeHtml(error.message)}</p>` +
        '<button type="button" class="retry-analysis">' +
        '<i class="fa-solid fa-rotate-right" aria-hidden="true"></i> Retry analysis</button>';

      const retry = this.el.analysis.querySelector('.retry-analysis');
      if (retry) retry.addEventListener('click', () => this.analyse());
      this.paintAnalysisButton();

      if (!automatic) this.status(error.message, false, true);
    } finally {
      this.refreshQuota();
    }
  }

  /**
   * Scroll the sidebar so the analysis panel is visible.
   *
   * The button is in the header and the panel is at the foot of a scrolling
   * sidebar, so pressing it otherwise produces no visible change at all: the
   * spinner, the result and any error all appear somewhere the reader is not
   * looking.
   */
  revealAnalysis() {
    const panel = this.el.analysis.closest('div');

    if (panel && panel.scrollIntoView) {
      panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  renderAnalysis(analysis) {
    // Entries cached before the notice existed are bare strings.
    const text = typeof analysis === 'string' ? analysis : analysis.text;
    const notice = typeof analysis === 'string' ? null : analysis.notice;

    const paragraphs = String(text)
      .split(/\n\n+/)
      .map((block) => {
        const b = block.trim();
        if (b.startsWith('### ')) {
          const nl = b.indexOf('\n');
          const head = (nl === -1 ? b.slice(4) : b.slice(4, nl)).trim();
          const rest = nl === -1 ? '' : b.slice(nl + 1).trim();
          return `<h4 class="analysis-head">${escapeHtml(head)}</h4>`
            + (rest ? `<p>${escapeHtml(rest)}</p>` : '');
        }
        return `<p>${escapeHtml(b)}</p>`;
      })
      .join('');

    this.el.analysis.innerHTML = paragraphs
      + (notice ? `<p class="analysis-notice">${escapeHtml(notice)}</p>` : '');
  }

  /* -------------------------------------------------------------- status */

  status(message, busy = false, isError = false) {
    const el = this.el.status;

    if (!message) {
      el.classList.add('hidden');
      return;
    }

    el.classList.remove('hidden');
    el.classList.toggle('error', isError);
    el.innerHTML = `${busy ? '<span class="spinner"></span>' : ''}<span>${escapeHtml(message)}</span>`
      + (busy ? '' : '<button type="button" class="status-dismiss" aria-label="Dismiss">✕</button>');

    const dismiss = el.querySelector('.status-dismiss');

    if (dismiss) {
      dismiss.addEventListener('click', () => this.status(null));
    }
  }
}

/* ---------------------------------------------------------------- markup */

/** Report filename, tracing back to the log it came from. */
function reportFilename(flight) {
  const source = flight.meta.filename;

  if (source) return `${source.replace(/\.[^.]+$/, '')}.pdf`;

  return `flight-${fmt.date(flight.meta.flownAt, flight.meta.timeZone).replace(/\s+/g, '-')}.pdf`;
}

/** The warning badge for a library row, or an empty string. */
function warningBadge(record) {
  const summary = fmt.warningSummary(record);

  if (summary === null) return '';

  return ` · <span class="flags ${summary.severity}" title="${escapeHtml(summary.detail)}">`
    + `${escapeHtml(summary.label)}</span>`;
}

/**
 * Section headings for the information panel.
 *
 * Kept in one place because a heading and its icon are a pair: defining them
 * at each call site is how the battery block ends up with a different icon
 * from the battery table two screens later.
 *
 * Duotone light for the descriptive sections and duotone solid for analysis,
 * which is the one thing on the panel that was written rather than measured.
 */
const SECTION_ICONS = {
  Warnings: 'fa-duotone fa-light fa-triangle-exclamation',
  Battery: 'fa-duotone fa-light fa-battery-quarter',
  Capture: 'fa-duotone fa-light fa-image',
  Equipment: 'fa-duotone fa-light fa-drone',
  Position: 'fa-duotone fa-light fa-satellite',
  Weather: 'fa-duotone fa-light fa-cloud-bolt-sun',
  Analysis: 'fa-duotone fa-solid fa-magnifying-glass-waveform',
};

/**
 * A panel heading with its icon.
 *
 * `label` may carry a number, as battery blocks do on a dual-battery
 * aircraft, so the icon is looked up on the first word.
 */
const WMO_CODES = {
  0: 'Clear', 1: 'Mainly clear', 2: 'Partly cloudy', 3: 'Overcast',
  45: 'Fog', 48: 'Rime fog',
  51: 'Light drizzle', 53: 'Drizzle', 55: 'Heavy drizzle',
  56: 'Freezing drizzle', 57: 'Freezing drizzle',
  61: 'Light rain', 63: 'Rain', 65: 'Heavy rain',
  66: 'Freezing rain', 67: 'Freezing rain',
  71: 'Light snow', 73: 'Snow', 75: 'Heavy snow', 77: 'Snow grains',
  80: 'Light showers', 81: 'Showers', 82: 'Violent showers',
  85: 'Snow showers', 86: 'Snow showers',
  95: 'Thunderstorm', 96: 'Thunderstorm, hail', 99: 'Thunderstorm, hail',
};

function weatherCodeText(code) {
  if (code === null || code === undefined) return null;
  return WMO_CODES[code] || null;
}

const WEATHER_ICON_BASE = 'https://static.dronedesk.io/assets/img/weather/';

// WMO code -> [day, night] Meteocons icon name (hosted on the CDN above).
const WEATHER_ICONS = {
  0: ['clear-day', 'clear-night'], 1: ['clear-day', 'clear-night'],
  2: ['partly-cloudy-day', 'partly-cloudy-night'], 3: ['overcast-day', 'overcast-night'],
  45: ['fog-day', 'fog-night'], 48: ['fog-day', 'fog-night'],
  51: ['drizzle', 'drizzle'], 53: ['drizzle', 'drizzle'], 55: ['drizzle', 'drizzle'],
  56: ['sleet', 'sleet'], 57: ['sleet', 'sleet'],
  61: ['rain', 'rain'], 63: ['rain', 'rain'], 65: ['extreme-rain', 'extreme-rain'],
  66: ['sleet', 'sleet'], 67: ['sleet', 'sleet'],
  71: ['snow', 'snow'], 73: ['snow', 'snow'], 75: ['snow', 'snow'], 77: ['snow', 'snow'],
  80: ['rain', 'rain'], 81: ['rain', 'rain'], 82: ['extreme-rain', 'extreme-rain'],
  85: ['snow', 'snow'], 86: ['snow', 'snow'],
  95: ['thunderstorms', 'thunderstorms'],
  96: ['thunderstorms-extreme', 'thunderstorms-extreme'], 99: ['thunderstorms-extreme', 'thunderstorms-extreme'],
};

function weatherIconUrl(code, isDay) {
  if (code === null || code === undefined || !WEATHER_ICONS[code]) return null;
  const name = WEATHER_ICONS[code][isDay === 0 ? 1 : 0];
  return `${WEATHER_ICON_BASE}${name}.svg`;
}

/** A plain-word descriptor for a Kp value, aligned to NOAA's G-scale. */
function kpLabel(kp) {
  if (kp < 5) return 'quiet';
  if (kp < 6) return 'minor storm';
  if (kp < 7) return 'moderate storm';
  if (kp < 8) return 'strong storm';
  if (kp < 9) return 'severe storm';
  return 'extreme storm';
}

function sectionHead(label, style = '') {
  const icon = SECTION_ICONS[String(label).split(' ')[0]];

  return `<div class="section-head"${style ? ` style="${style}"` : ''}>`
    + `<span class="panel-label">`
    + (icon ? `<i class="${icon}" aria-hidden="true"></i>` : '')
    + `${escapeHtml(label)}</span></div>`;
}

/** The bare number, with its unit stripped for a label that already shows it. */
function bare(formatted) {
  return String(formatted).split(' ')[0];
}

function unitSuffix(kind) {
  if (kind === null) return '';
  if (kind === 'percent') return ' (%)';

  // Height is measured from the take-off point, which equals above ground
  // level only over flat terrain. AGL is the term operators use, and the
  // distinction is called out in the parser notes rather than the label.
  if (kind === 'altitude') return ` (${units.label('altitude')} AGL)`;

  return ` (${units.label(kind)})`;
}

const POINTS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
                'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];

function compassPoint(degrees) {
  return POINTS[Math.round((degrees % 360) / 22.5) % 16];
}

function stat(label, value) {
  return `<div class="stat"><div class="panel-label">${label}</div><div class="stat-value">${value}</div></div>`;
}

function kv(label, value, className = '') {
  return `<div class="kv"><dt>${label}</dt><dd class="${className}">${value}</dd></div>`;
}

/**
 * The library thumbnail: the flight's own shape, normalised into a 52x34 box.
 * Pilots recognise their flights by shape long before they read a filename.
 */
function thumbnail(path) {
  if (!path || path.length < 2) return '';

  const lats = path.map((p) => p[0]);
  const lons = path.map((p) => p[1]);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLon = Math.min(...lons), maxLon = Math.max(...lons);
  const spanLat = maxLat - minLat || 1e-6;
  const spanLon = maxLon - minLon || 1e-6;
  const scale = Math.min(46 / spanLon, 28 / spanLat);

  const points = path
    .map((p) => {
      const x = 26 + (p[1] - (minLon + spanLon / 2)) * scale;
      const y = 17 - (p[0] - (minLat + spanLat / 2)) * scale;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return `<svg viewBox="0 0 52 34" width="52" height="34" aria-hidden="true">
    <polyline points="${points}" fill="none" stroke="#038AFF" stroke-width="1.2"
      stroke-linejoin="round" stroke-linecap="round"/>
  </svg>`;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Boot.
 *
 * The promise is exported so failures are observable rather than becoming an
 * unhandled rejection. start() is async, and a fire-and-forget call means any
 * error during wiring leaves a dead page with a single console line and no
 * indication to the user that anything went wrong.
 */
const app = new App();

/** Exported for tests. Nothing in the application reads it. */
export { app };
export const ready = app.start();

ready.catch((error) => {
  console.error('[logviewer] failed to start:', error);

  const status = document.getElementById('status');

  if (status) {
    status.classList.remove('hidden');
    status.classList.add('error');
    status.textContent = 'The viewer failed to start. Reload the page, and check the console if it persists.';
  }
});
